import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { GetMedicalHistory, GetProcedureCodeList } from "./Service";
import { getSessionData } from "../../helpers/Util";
import "../../styles/MedicalHistory.css";
import { CheckConditionForViewClick } from "./MedicalHistoryPdf";
import toast from "react-hot-toast";
import PdfView from "../../Component/shared/pdfView/PdfView";
import { set } from "react-hook-form";

/* ─── helpers ────────────────────────────────────────────── */
const getVisitLabel = (type = "") => {
  if (type === "1") return { label: "IE", cls: "mh-visit-badge--new" };
  if (type === "2") return { label: "FU", cls: "mh-visit-badge--new" };
  if (type === "3") return { label: "RE", cls: "mh-visit-badge--new" };
  return { label: type || "—", cls: "mh-visit-badge--default" };
};

const getBilledClass = (isBilled) =>
  isBilled ? "mh-status-pill--done" : "mh-status-pill--waiting";

const formatDate = (raw = "") => {
  if (!raw) return "—";
  try {
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
  } catch { return raw; }
};

/* ─── Clean SVG document/view icon ──────────────────────── */
const DocViewIcon = () => (
  <svg
    width="16" height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {/* Document outline */}
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    {/* Fold corner */}
    <polyline points="14 2 14 8 20 8" />
    {/* Lines representing content */}
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);

/* ─── Skeleton — table mode ──────────────────────────────── */
const SkeletonTableRows = ({ count = 4 }) =>
  Array.from({ length: count }).map((_, i) => (
    <div className="mh-skeleton-row" key={i}>
      <div className="mh-skeleton-cell mh-skeleton-cell--sm" />
      <div className="mh-skeleton-cell mh-skeleton-cell--md" />
      <div className="mh-skeleton-cell mh-skeleton-cell--lg" />
      <div className="mh-skeleton-cell mh-skeleton-cell--sm" />
      <div className="mh-skeleton-cell mh-skeleton-cell--md" />
      <div className="mh-skeleton-cell mh-skeleton-cell--sm" />
    </div>
  ));

/* ─── Skeleton — card mode ───────────────────────────────── */
const SkeletonCards = ({ count = 3 }) =>
  Array.from({ length: count }).map((_, i) => (
    <div className="mh-skeleton-card" key={i}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
        <div className="mh-skeleton-cell mh-skeleton-cell--sm" style={{ height: 13 }} />
        <div className="mh-skeleton-cell mh-skeleton-cell--sm" style={{ width: 60, height: 13 }} />
      </div>
      <div className="mh-skeleton-cell mh-skeleton-cell--md" style={{ height: 15 }} />
      <div className="mh-skeleton-cell mh-skeleton-cell--lg" style={{ height: 12 }} />
      <div className="mh-skeleton-cell" style={{ width: "70%", height: 12 }} />
      <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 8, borderTop: "1px solid #e5ddd3", gap: 8 }}>
        <div className="mh-skeleton-cell" style={{ width: 60, height: 22, borderRadius: 20 }} />
        <div className="mh-skeleton-cell" style={{ width: 68, height: 22, borderRadius: 20 }} />
      </div>
    </div>
  ));

/* ─── Treatment list (shared) ────────────────────────────── */
const TreatmentList = ({ procedures }) => {
  if (!procedures || procedures.length === 0) {
    return <span style={{ color: "var(--mh-light)", fontSize: "12px" }}>—</span>;
  }
  return (
    <ul className="mh-treatment-list">
      {procedures.map((proc, pIdx) => (
        <li key={proc.Code ?? pIdx} className="mh-treatment-item">
          <span className="mh-treatment-bullet" />
          {proc.Code && <span className="mh-treatment-code">{proc.Code}</span>}
          <span className="mh-treatment-desc">{proc.Name || "—"}</span>
        </li>
      ))}
    </ul>
  );
};

/* ─── Mobile card row ────────────────────────────────────── */
const MobileCard = ({ row, onView }) => {
  const date       = row.Date ?? "";
  const doctorName = row.Physician?.Name ?? "—";
  const location   = row.Patient?.Location ?? "";
  const procedures = Array.isArray(row.Procedure) ? row.Procedure : [];
  const doctorCell = location ? `${doctorName} - ${location}` : doctorName;

  const { label: visitLabel, cls: visitCls } = getVisitLabel(row.VisitType);
  const billedLabel = row.IsBilled ? "Done" : "Waiting";
  const billedCls   = getBilledClass(row.IsBilled);
  const canView     = row.IsFinalized === true && row.HasVisitDocument === true;

  return (
    <div className="mh-card">
      {/* Top row: date + badges */}
      <div className="mh-card-top">
        <span className="mh-card-date">{formatDate(date)}</span>
        <div className="mh-card-badges">
          <span className={`mh-visit-badge ${visitCls}`}>{visitLabel}</span>
        </div>
      </div>

      {/* Doctor */}
      <div className="mh-card-doctor">{doctorCell}</div>

      {/* Treatments */}
      {procedures.length > 0 && (
        <div className="mh-card-treatments">
          <TreatmentList procedures={procedures} />
        </div>
      )}

      {/* Footer: billed status label + view button */}
      <div className="mh-card-footer">
        <div className="mh-card-footer-left">
          <span className="mh-card-label">Billed</span>
          <span className={`mh-status-pill ${billedCls}`}>{billedLabel}</span>
        </div>
        {canView ? (
          <button
            className="mh-pdf-btn"
            title="View Medical Note"
            aria-label="View Medical Note"
            onClick={() => onView(row)}
          >
            <DocViewIcon />
          </button>
        ) : (
          <span style={{ color: "var(--mh-light)", fontSize: "18px" }}>—</span>
        )}
      </div>
    </div>
  );
};

/* ─── Main component ─────────────────────────────────────── */
const MedicalHistory = ({ isOpen, onClose }) => {
  const shouldRender = isOpen === undefined ? true : isOpen;

  const data = getSessionData("tokenData", {});

  // ── ref on the portal root — passed to PdfView so Bootstrap mounts inside our stacking context ──
  const portalRootRef = useRef(null);

  const [history, setHistory]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [selectVisit, setSelectVisit] = useState("2");
  const [statusLine, setStatusLine]   = useState("");
  const [examDate, setExamDate]       = useState("");
  const [path, setPath]               = useState("");
  const [isRealDataVisible, setIsRealDataVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const buildStatusLine = useCallback((appointments) => {
    let billedCount = 0, scheduledCount = 0, finalizedCount = 0;
    for (let i = 0; i < appointments.length; i++) {
      if (appointments[i].IsBilled === true)         billedCount++;
      else if (appointments[i].IsFinalized === true) finalizedCount++;
      else                                           scheduledCount++;
    }
    let line = "";
    if (billedCount    !== 0) line += billedCount    + "-Billed ";
    if (finalizedCount !== 0) line += finalizedCount + "-Finalized ";
    if (scheduledCount !== 0) line += scheduledCount + "-Scheduled ";
    line += " out of " + appointments.length + " appointments.";
    return line;
  }, []);


  const GetProcedureCode = (id) => {
    const procedureList = getSessionData("procedureGroupList", [])
    let obj = procedureList?.find((item) => item.procedureGroupId === id) || {};
    return obj?.procedureGroupCode;
  };

  const CallGetMedicalHistoryApi = useCallback(async (filterType) => {
    const request = {

  "User": {
    "Account": {
      "ID": data.companyId,
      "Name": data.companyName,
      "Type": "billing"
    },
    "Specialty": {
      "Name": data.doctorSpecialtyName,
      "Code": GetProcedureCode(data.doctorSpecialtyId),
      "ID": data.doctorSpecialtyId
    },
    "UserName": data?.userName || 'greenbills',
    "Physician": {
      "FilterType": 1,
      "FilterValue": 1,
      "ID": data.doctorId
    },
    "ID": data?.userId || 'US000000000000000079',
    "Domain": "greenyourbills.com"
  },
  "Appointment": {
    "Patient": {
      "CaseId": data.caseId
    }
  }
    };

    setLoading(true);
    setError(null);
    try {
      const response = await GetMedicalHistory(request);
      const rows = response?.Data?.[0]?.Appointment ?? [];

      if (!Array.isArray(rows)) {
        setHistory([]); setStatusLine(""); setExamDate("");
        return;
      }
      const sorted = [...rows].sort(
        (a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime()
      );
      const latestNonFU = sorted.find((v) => v?.VisitType !== "2" && v?.Date) || null;

      setHistory(sorted);
      setStatusLine(buildStatusLine(sorted));
      setExamDate(latestNonFU ? latestNonFU.Date : "N/A");
    } catch (err) {
      console.error("Error fetching medical history:", err);
      setError("Failed to load medical history. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [buildStatusLine]);

  useEffect(() => {
    if (shouldRender) CallGetMedicalHistoryApi(selectVisit);
  }, [shouldRender, selectVisit, CallGetMedicalHistoryApi]);

  useEffect(() => {
    if (!shouldRender) return;
    const handler = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [shouldRender, onClose]);

  useEffect(() => {
    if (shouldRender) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [shouldRender]);

  const handleViewClick = async (row) => {
    setLoading(true);
    setSelectedRow(row);
    let result = "";
    result = await CheckConditionForViewClick(row, GetProcedureCode);
    CheckResult(result);
    setLoading(false);
  }

  const  CheckResult = (result) => {
    try {
      if (!result) {
      } else if (result.HasException === true) {
        setLoading(false);
        console.error("API error:", result.ExceptionMessage || "Unknown error");
        toast.error("An error occurred while fetching the document.");
      } else if (result.Data == null) {
        setLoading(false);
        toast.error("An error occurred while fetching the document.");

      } else {
        ShowPdf(result.Data[0][1]);
      }
    } catch (error) { }
  };

  const ShowPdf = async (url) => {
    setIsRealDataVisible(true);
    setPath(url);
  }

  const OnHidePdfView = () => {
    setIsRealDataVisible(false);
  }

  if (!shouldRender) return null;

  const modalContent = (
    // ── portalRootRef attached here — Bootstrap Modal will mount inside this node ──
    <div className="mh-root" ref={portalRootRef}>
      <div
        className="mh-overlay"
        role="dialog"
        aria-modal="true"
        aria-label="Medical History"
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div className="mh-modal">

          {/* ── Header ── */}
          <div className="mh-header">
            <div className="mh-header-left">
              <div className="mh-header-titles">
                <div className="mh-header-title">Medical History</div>
              </div>
            </div>
            <button className="mh-close-btn" onClick={onClose} aria-label="Close">✕</button>
          </div>

          {/* ── Body ── */}
          <div className="mh-body">
            {loading ? (
              <>
                {/* Table skeleton — hidden on mobile via CSS */}
                <div className="mh-skeleton-table">
                  <div className="mh-table-wrap" style={{ background: "var(--mh-card-bg)" }}>
                    <table className="mh-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Doctor Name</th>
                          <th>Treatment</th>
                          <th>Visit Type</th>
                          <th>Billed</th>
                          <th>View</th>
                        </tr>
                      </thead>
                    </table>
                    <SkeletonTableRows count={4} />
                  </div>
                </div>
                <div className="mh-skeleton-cards">
                  <SkeletonCards count={3} />
                </div>
              </>
            ) : error ? (
              <div className="mh-state-wrap">
                <div className="mh-state-icon">⚠️</div>
                <div className="mh-state-title">Unable to load records</div>
                <div className="mh-state-msg">{error}</div>
                <button
                  className="mh-pdf-btn"
                  style={{ width: "auto", padding: "8px 20px", fontSize: "12px", fontWeight: 700, gap: 6, marginTop: 8 }}
                  onClick={() => CallGetMedicalHistoryApi(selectVisit)}
                >
                  ↻ Retry
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="mh-state-wrap">
                <div className="mh-state-icon">📋</div>
                <div className="mh-state-title">No records found</div>
                <div className="mh-state-msg">
                  No medical history records are available for this patient.
                </div>
              </div>
            ) : (
              <>
                {/* ── TABLE VIEW (≥ 640px) ── */}
                <div className="mh-table-view">
                  <div className="mh-table-wrap">
                    <table className="mh-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Doctor Name</th>
                          <th>Treatment</th>
                          <th>Visit Type</th>
                          <th>Billed</th>
                          <th>View</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((row, idx) => {
                          const date       = row.Date ?? "";
                          const doctorName = row.Physician?.Name ?? "—";
                          const location   = row.Patient?.Location ?? "";
                          const procedures = Array.isArray(row.Procedure) ? row.Procedure : [];
                          const doctorCell = location ? `${doctorName} - ${location}` : doctorName;

                          const { label: visitLabel, cls: visitCls } = getVisitLabel(row.VisitType);
                          const billedLabel = row.IsBilled ? "Done" : "Waiting";
                          const billedCls   = getBilledClass(row.IsBilled);
                          const canView     = row.IsFinalized === true && row.HasVisitDocument === true;

                          return (
                            <tr key={row.ID ?? idx}>
                              <td className="mh-cell-date">{formatDate(date)}</td>
                              <td className="mh-cell-doctor">
                                <div className="mh-doctor-name">{doctorCell}</div>
                              </td>
                              <td className="mh-cell-treatment">
                                <TreatmentList procedures={procedures} />
                              </td>
                              <td className="mh-cell-type">
                                <span className={`mh-visit-badge ${visitCls}`}>{visitLabel}</span>
                              </td>
                              <td className="mh-cell-billed">
                                <span className={`mh-status-pill ${billedCls}`}>{billedLabel}</span>
                              </td>
                              <td className="mh-cell-view">
                                {canView ? (
                                  <button
                                    className="mh-pdf-btn"
                                    title="View Medical Note"
                                    aria-label="View Medical Note"
                                    onClick={() => handleViewClick(row)}
                                  >
                                    <DocViewIcon />
                                  </button>
                                ) : (
                                  <span style={{ color: "var(--mh-light)", fontSize: "18px" }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── CARD VIEW (< 640px) ── */}
                <div className="mh-cards-view mh-cards">
                  {history.map((row, idx) => (
                    <MobileCard
                      key={row.ID ?? idx}
                      row={row}
                      onView={handleViewClick}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="mh-footer">
            {!loading && !error && history.length > 0 && (
              <p
                className="mh-summary"
                dangerouslySetInnerHTML={{
                  __html: statusLine.replace(
                    /(\d+)-(Billed|Finalized|Scheduled)/g,
                    "<strong>$1</strong>-$2"
                  )
                }}
              />
            )}
            <button type="button" className="mh-footer-close" onClick={onClose}>
              Close
            </button>
          </div>

        </div>
      </div>

      {/* ── PdfView inside portal; container prop makes Bootstrap mount here ── */}
      {isRealDataVisible && 
      <PdfView 
      path={path} 
      row={selectedRow} 
      isRealDataVisible={isRealDataVisible}
      OnHide={OnHidePdfView}
      GetProcedureCode={GetProcedureCode}
      />}

    </div>
  );

  return createPortal(modalContent, document.body);
};

export default MedicalHistory;